import * as THREE from "three";
import { WallType } from "../types/floorplannerTypes";
import { getQuadraticBezierPoint, projectPointOntoArc } from "./arcUtils";

export type WallAddAttachEndpoint = "start" | "end" | "middle";

export type WallHoverAddButtonPose = {
  /** Position where the new wall should start (on the wall face toward the pointer). */
  startPoint: THREE.Vector2;
  /**
   * Visual "+" position: on the wall centerline under the hover
   * (centered through the wall thickness, not offset to either side).
   */
  buttonPoint: THREE.Vector2;
  attachEndpoint: WallAddAttachEndpoint;
  /** Centerline projection used for middle edge connections. */
  projectedPoint: THREE.Vector2;
  sideSign: 1 | -1;
  /** Unit direction to grow the first wall segment away from the source wall. */
  outwardDirection: THREE.Vector2;
};

/** Minimum first-segment length when starting a wall from the hover "+" (meters). */
export const WALL_ADD_MIN_INITIAL_LENGTH = 0.5;

const MIN_WALL_LENGTH_FOR_ADD_BUTTON = 0.25;
/** Keep the chip away from endpoints so it does not fight corner handles. */
const ENDPOINT_INSET_RATIO = 0.08;
const ENDPOINT_INSET_MIN = 0.08;

/**
 * World-space hit radius for the "+" chip (easy to click, zoom-aware).
 */
export const getWallAddButtonRadius = (
  _wallWidth: number,
  zoomDivisor: number = 1,
): number => {
  const zoomRadius = 0.09 / Math.max(zoomDivisor, 0.5);
  return THREE.MathUtils.clamp(zoomRadius, 0.05, 0.12);
};

const projectOntoStraightWall = (
  pointerWorld: THREE.Vector2,
  start: THREE.Vector2,
  end: THREE.Vector2,
): { point: THREE.Vector2; tangent: THREE.Vector2 } | null => {
  const tangent = end.clone().sub(start);
  const length = tangent.length();
  if (length < 1e-6) {
    return null;
  }
  tangent.normalize();
  const inset = Math.min(length * 0.45, Math.max(ENDPOINT_INSET_MIN, length * ENDPOINT_INSET_RATIO));
  const along = THREE.MathUtils.clamp(
    pointerWorld.clone().sub(start).dot(tangent),
    inset,
    length - inset,
  );
  return {
    point: start.clone().addScaledVector(tangent, along),
    tangent,
  };
};

/**
 * Project hover onto the wall centerline and park the "+" there.
 * Button stays centered on the wall thickness; pointer side only picks growth direction.
 */
export const getWallHoverAddButtonPose = (
  wall: WallType,
  pointerWorld: THREE.Vector2,
  wallWidth: number,
  _zoomDivisor: number = 1,
): WallHoverAddButtonPose | null => {
  const length = wall.start.distanceTo(wall.end);
  if (length < MIN_WALL_LENGTH_FOR_ADD_BUTTON) {
    return null;
  }

  let projectedPoint: THREE.Vector2;
  let tangent: THREE.Vector2;

  if (wall.lineForm === "arc" && wall.controlPoint) {
    const projection = projectPointOntoArc(
      pointerWorld,
      wall.start,
      wall.controlPoint,
      wall.end,
    );
    const t = THREE.MathUtils.clamp(projection.t, ENDPOINT_INSET_RATIO, 1 - ENDPOINT_INSET_RATIO);
    projectedPoint = getQuadraticBezierPoint(t, wall.start, wall.controlPoint, wall.end);
    const a = getQuadraticBezierPoint(Math.max(0, t - 0.02), wall.start, wall.controlPoint, wall.end);
    const b = getQuadraticBezierPoint(Math.min(1, t + 0.02), wall.start, wall.controlPoint, wall.end);
    tangent = b.sub(a);
    if (tangent.lengthSq() < 1e-12) {
      tangent = wall.end.clone().sub(wall.start);
    }
  } else {
    const projection = projectOntoStraightWall(pointerWorld, wall.start, wall.end);
    if (!projection) {
      return null;
    }
    projectedPoint = projection.point;
    tangent = projection.tangent;
  }

  if (tangent.lengthSq() < 1e-12) {
    return null;
  }
  tangent.normalize();
  const perp = new THREE.Vector2(-tangent.y, tangent.x);

  const toPointer = pointerWorld.clone().sub(projectedPoint);
  const sideDot = toPointer.dot(perp);
  const sideSign: 1 | -1 = sideDot >= 0 ? 1 : -1;

  const effectiveWidth = wall.wallWidth ?? wallWidth;
  const halfWidth = effectiveWidth / 2;
  const outwardDirection = perp.clone().multiplyScalar(sideSign);
  const startPoint = projectedPoint.clone().add(outwardDirection.clone().multiplyScalar(halfWidth));

  return {
    startPoint,
    buttonPoint: projectedPoint.clone(),
    attachEndpoint: "middle",
    projectedPoint: projectedPoint.clone(),
    sideSign,
    outwardDirection,
  };
};
